/**
 * Hook del detalle de un Pack de Energía (US-2.4). La red vive en `packApi`; acá
 * solo el estado de la vista (loading / no encontrado / error / refresh).
 */
import { useCallback, useEffect, useState } from 'react';
import type { EnergyPack } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { packApi } from '../api/packApi';

export interface UsePackResult {
  pack: EnergyPack | null;
  loading: boolean;
  error: string | null;
  /** `true` cuando el backend responde 404 (pack inexistente). */
  notFound: boolean;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar el pack.';
}

export function usePack(packId: string | undefined): UsePackResult {
  const [pack, setPack] = useState<EnergyPack | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!packId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);

    packApi
      .get(packId)
      .then((result) => {
        if (!cancelled) setPack(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setNotFound(true);
        } else {
          setError(toErrorMessage(caught));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [packId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { pack, loading, error, notFound, refresh };
}
